import { useMemo } from 'react';
import { useThemeColors, useThemeStore } from '../../shared/theme';
import { useTranslation } from '../../shared/i18n';
import {
  buildTabLabelsFromRegistry,
  buildTabRoutesFromRegistry,
  type MainTabModel,
} from '../../shared/navigation/tabRegistry';
import { resolveMenuTabComponent, resolveMenuTabIconFlow, type MenuTabBarIconFlow } from '../../shared/navigation/menuTabResolver';
import { useMenuTabFlowKind, useOnlineOrdersFeatureEnabled } from '../../data/domains/config';

export type { MainTabModel, MenuTabBarIconFlow };

export function useMainTabModel(): MainTabModel {
  const colors = useThemeColors() ?? useThemeStore.getState().colors;
  const { t } = useTranslation();
  const menu_tab_flow_kind = useMenuTabFlowKind();
  const menu_tab_screen = useMemo(
    () => resolveMenuTabComponent(menu_tab_flow_kind),
    [menu_tab_flow_kind],
  );
  const show_online_orders = useOnlineOrdersFeatureEnabled();

  const tab_routes = useMemo(
    () =>
      buildTabRoutesFromRegistry({ menu_flow_kind: menu_tab_flow_kind }, show_online_orders).map(route =>
        route.name === 'DailyMenuFlow' ? { ...route, component: menu_tab_screen } : route,
      ),
    [menu_tab_screen, show_online_orders, menu_tab_flow_kind],
  );

  const tab_labels = useMemo(
    () => buildTabLabelsFromRegistry(t, menu_tab_flow_kind),
    [t, menu_tab_flow_kind],
  );

  return useMemo(
    (): MainTabModel => ({
      primary: colors.primary,
      screen_bg: colors.background,
      tab_labels,
      tab_routes,
      menu_tab_flow: resolveMenuTabIconFlow(menu_tab_flow_kind),
    }),
    [colors.primary, colors.background, tab_labels, tab_routes, menu_tab_flow_kind],
  );
}
